import { createContext, useContext, useState } from "react";

export const translations = {
  en: {
    nav: { home: "Home", about: "About", campaigns: "Campaigns", suggest: "Suggest a Campaign", membership: "Become a Member", contact: "Contact", donate: "Donate" },
    suggest: {
      overline: "Your idea, our mission",
      title: "Suggest a campaign",
      sub: "Have an idea that could change lives? Tell us about the cause, how it would be funded and how the funds would be used.",
      note: "Every suggestion lands directly in our team's inbox at contact@future-gen.eu. We review each one and get back to you personally.",
      memberRequired: "Please note: to suggest a campaign, you must be a member of Future Gen ASBL.",
      memberRequiredLink: "Not a member yet? Join us here",
      campaignTitle: "Campaign title",
      description: "Campaign description — what is the cause, who does it help, why now?",
      objective: "Campaign objective",
      fundSource: "Fund source",
      srcDonation: "Donation",
      srcProduct: "Product sales",
      srcService: "Service sales",
      srcOther: "Other",
      fundDistribution: "Fund distribution",
      fundDistributionPh: "How would funds be used?",
      contactSection: "How can we reach you?",
      contactName: "Contact name",
      contactEmail: "Contact email",
      contactPhone: "Contact phone",
      submit: "Submit suggestion",
      successTitle: "Thank you!",
      success: "Your suggestion has been sent to our team. We will get back to you soon.",
    },
    hero: {
      overline: "Future Gen ASBL — Luxembourg · Youth · Education · Solidarity",
      lines: ["THE FUTURE", "IS BUILT BY", "THE YOUNG."],
      sub: "We are a non-profit registered in Luxembourg fighting for youth development, education access and poverty relief. Every campaign is concrete, every euro is traced.",
      ctaPrimary: "Support a campaign",
      ctaSecondary: "Read our manifesto",
    },
    marquee: ["FUTURE GEN ASBL", "EMPOWERING YOUTH", "EDUCATION FOR ALL", "FIGHTING POVERTY"],
    stats: { raised: "raised for our campaigns", campaigns: "campaigns run to date", members: "active members", donations: "donations received" },
    pillars: {
      overline: "What we fight for",
      title: "Three battles, one generation.",
      items: [
        { num: "01", title: "Youth Development", text: "Mentorship, skills labs and safe spaces where young people build confidence, craft and a network that carries them into work." },
        { num: "02", title: "Education Access", text: "School kits, tutoring, scholarships and digital classrooms — because talent is everywhere, but opportunity is not." },
        { num: "03", title: "Poverty Relief", text: "Warm meals, winter kits and shelter referrals. Immediate dignity for neighbours who cannot wait for systemic change." },
      ],
    },
    featured: { overline: "Campaigns of the moment", title: "Where your euro goes today.", viewAll: "All campaigns", raised: "raised", of: "of", support: "Support this campaign", completed: "Completed" },
    manifesto: {
      overline: "Our manifesto",
      title: "We don't inherit the future. We build it.",
      text: "Future Gen ASBL was founded on a simple conviction: the generation that will live tomorrow deserves a seat at the table today. We fund, teach and stand beside the young — in classrooms, in workshops, in the street.",
      cta: "Discover who we are",
    },
    ctaBand: { title: "Ready to move from witness to builder?", text: "Join as a member from €10/month, or give once to a campaign that speaks to you.", member: "Become a member", donate: "See campaigns" },
    about: {
      overline: "About Future Gen ASBL",
      title: "A non-profit written in the future tense.",
      intro: "Registered in Luxembourg, Future Gen ASBL is a non-profit association working at the intersection of youth development, education and the fight against poverty. We are volunteers, educators and neighbours — accountable to our members and transparent about every euro.",
      chapters: [
        { num: "01", title: "We start with the young", text: "Every programme begins with a question: what does a 15-year-old in this neighbourhood need to believe in their own future? The answer shapes the budget, not the other way round." },
        { num: "02", title: "Education is infrastructure", text: "Kits, tutors, laptops, scholarships. We treat learning material like roads and bridges — public goods that compound for decades." },
        { num: "03", title: "Dignity before charity", text: "A warm meal served with respect. A winter kit without a lecture. We meet people where they are, without conditions attached to compassion." },
        { num: "04", title: "Radical transparency", text: "Every campaign publishes its goal, its raised amount and its results. Our accounts are presented annually to the general assembly of members." },
      ],
      legalTitle: "Legal identity",
      legalText: "Future Gen ASBL · Non-profit association registered in Luxembourg · contact@future-gen.eu · future-gen.eu",
      imageCaption: "Our volunteers on a Sunday skills lab.",
    },
    campaignsPage: {
      overline: "Campaigns",
      title: "Concrete goals. Tracked euros.",
      sub: "Each campaign has a target, a purpose and a public counter. Visit one, see exactly what your contribution funds.",
      current: "Current campaigns",
      past: "Past campaigns",
      raised: "raised",
      of: "of",
      goal: "goal",
      completed: "Completed",
      explore: "Visit campaign",
    },
    detail: {
      back: "All campaigns",
      purpose: "What it funds",
      raisedOf: "raised of",
      goal: "goal",
      donateTitle: "Contribute",
      cardTab: "Card (Stripe)",
      bankTab: "Bank transfer",
      name: "Your name",
      email: "Your email",
      amount: "Amount (EUR)",
      suggested: "Suggested",
      stripeNote: "You will choose the exact amount on the secure Stripe page.",
      donateNow: "Donate securely",
      bankInfo: "Transfer to our account with the reference below — we confirm your pledge once received.",
      iban: "IBAN",
      bic: "BIC",
      accountName: "Account name",
      pledgeBtn: "Register my pledge",
      pledgeThanks: "Pledge registered! Use this reference for your transfer:",
      reference: "Reference",
      loading: "Loading…",
      updates: "Campaign updates",
      donorWall: "Recent supporters",
      share: "Share this campaign",
      copied: "Link copied!",
      anonymous: "Anonymous",
      anonymousLabel: "Make my donation anonymous — my name will not appear in the supporters list",
    },
    membership: {
      overline: "Membership",
      title: "Become a member of Future Gen.",
      sub: "Members fund the backbone of the association, vote at the general assembly and receive our quarterly impact letter.",
      monthly: { name: "Monthly", price: "€10", period: "/month", note: "Flexible, cancel anytime" },
      yearly: { name: "Yearly", price: "€100", period: "/year", note: "One gesture for a whole year" },
      choosePlan: "Choose your contribution",
      details: "Your details",
      name: "Full name",
      email: "Email address",
      method: "Payment method",
      card: "Card — recurring via Stripe",
      bank: "Bank transfer — standing order",
      submitCard: "Continue to secure payment",
      submitBank: "Register & get bank details",
      successTitle: "Welcome aboard!",
      successText: "Your registration is recorded. Set up your standing order with the reference below and we will activate your membership on receipt.",
      benefitsTitle: "Membership includes",
      benefits: ["Vote at the annual general assembly", "Submit campaign suggestions", "Quarterly impact letter", "Invitations to member events", "Full annual financial report"],
    },
    contact: {
      overline: "Contact",
      title: "Write to us. We answer.",
      sub: "A question, a partnership idea, a school that needs us? This inbox is read by humans.",
      name: "Your name",
      email: "Your email",
      subject: "Subject",
      message: "Your message",
      send: "Send message",
      success: "Message sent — we will reply shortly.",
      addressLabel: "Visit us",
      address: "Luxembourg",
      emailLabel: "Email",
    },
    payment: {
      successTitle: "Thank you. Truly.",
      successText: "Your payment is confirmed. You just moved a counter that matters.",
      pendingTitle: "Confirming your payment…",
      pendingText: "We are checking with Stripe. This takes a few seconds.",
      failTitle: "Payment not confirmed yet",
      failText: "Your bank may still be processing. If you were charged, it will appear shortly.",
      backHome: "Back to home",
      cancelledTitle: "Payment cancelled",
      cancelledText: "No charge was made. The campaign will still be here when you are ready.",
      tryAgain: "Back to campaigns",
    },
    footer: {
      tagline: "Building the generation that builds the future.",
      explore: "Explore",
      contactTitle: "Contact",
      legalTitle: "Legal",
      legal: "Future Gen ASBL · Non-profit association registered in Luxembourg",
      newsletterTitle: "Impact newsletter",
      newsletterPlaceholder: "Your email",
      newsletterBtn: "Subscribe",
      newsletterThanks: "You're in — thank you!",
      rights: "All rights reserved.",
    },
    common: { loading: "Loading…" },
  },
  fr: {
    nav: { home: "Accueil", about: "À propos", campaigns: "Campagnes", suggest: "Proposer une campagne", membership: "Devenir membre", contact: "Contact", donate: "Faire un don" },
    suggest: {
      overline: "Votre idée, notre mission",
      title: "Proposer une campagne",
      sub: "Une idée qui pourrait changer des vies ? Parlez-nous de la cause, de son financement et de l'utilisation des fonds.",
      note: "Chaque proposition arrive directement dans la boîte de notre équipe à contact@future-gen.eu. Nous examinons chacune d'elles et revenons vers vous personnellement.",
      memberRequired: "À noter : pour proposer une campagne, vous devez être membre de Future Gen ASBL.",
      memberRequiredLink: "Pas encore membre ? Rejoignez-nous ici",
      campaignTitle: "Titre de la campagne",
      description: "Description de la campagne — quelle est la cause, qui aide-t-elle, pourquoi maintenant ?",
      objective: "Objectif de la campagne",
      fundSource: "Source de financement",
      srcDonation: "Don",
      srcProduct: "Vente de produits",
      srcService: "Vente de services",
      srcOther: "Autre",
      fundDistribution: "Répartition des fonds",
      fundDistributionPh: "Comment les fonds seraient-ils utilisés ?",
      contactSection: "Comment vous joindre ?",
      contactName: "Nom du contact",
      contactEmail: "E-mail du contact",
      contactPhone: "Téléphone du contact",
      submit: "Envoyer la proposition",
      successTitle: "Merci !",
      success: "Votre proposition a été envoyée à notre équipe. Nous reviendrons vers vous rapidement.",
    },
    hero: {
      overline: "Future Gen ASBL — Luxembourg · Jeunesse · Éducation · Solidarité",
      lines: ["L'AVENIR", "SE CONSTRUIT", "PAR LA JEUNESSE."],
      sub: "Nous sommes une ASBL enregistrée au Luxembourg, engagée pour la jeunesse, l'accès à l'éducation et la lutte contre la pauvreté. Chaque campagne est concrète, chaque euro est tracé.",
      ctaPrimary: "Soutenir une campagne",
      ctaSecondary: "Lire notre manifeste",
    },
    marquee: ["FUTURE GEN ASBL", "LA JEUNESSE AU POUVOIR", "L'ÉDUCATION POUR TOUS", "CONTRE LA PAUVRETÉ"],
    stats: { raised: "collectés pour nos campagnes", campaigns: "campagnes menées à ce jour", members: "membres actifs", donations: "dons reçus" },
    pillars: {
      overline: "Nos combats",
      title: "Trois batailles, une génération.",
      items: [
        { num: "01", title: "Développement de la jeunesse", text: "Mentorat, ateliers de compétences et lieux sûrs où les jeunes construisent confiance, savoir-faire et un réseau qui les porte vers l'emploi." },
        { num: "02", title: "Accès à l'éducation", text: "Kits scolaires, tutorat, bourses et classes numériques — parce que le talent est partout, mais pas les opportunités." },
        { num: "03", title: "Lutte contre la pauvreté", text: "Repas chauds, kits d'hiver et orientation vers des hébergements. Une dignité immédiate pour des voisins qui ne peuvent pas attendre." },
      ],
    },
    featured: { overline: "Campagnes du moment", title: "Où va votre euro aujourd'hui.", viewAll: "Toutes les campagnes", raised: "collectés", of: "sur", support: "Soutenir cette campagne", completed: "Terminée" },
    manifesto: {
      overline: "Notre manifeste",
      title: "Nous n'héritons pas du futur. Nous le construisons.",
      text: "Future Gen ASBL est née d'une conviction simple : la génération qui vivra demain mérite une place à la table aujourd'hui. Nous finançons, enseignons et marchons aux côtés des jeunes — en classe, en atelier, dans la rue.",
      cta: "Découvrir qui nous sommes",
    },
    ctaBand: { title: "Prêt·e à passer de témoin à bâtisseur ?", text: "Devenez membre dès 10 €/mois, ou donnez une fois à la campagne qui vous parle.", member: "Devenir membre", donate: "Voir les campagnes" },
    about: {
      overline: "À propos de Future Gen ASBL",
      title: "Une ASBL écrite au futur.",
      intro: "Enregistrée au Luxembourg, Future Gen ASBL est une association sans but lucratif qui agit à la croisée du développement de la jeunesse, de l'éducation et de la lutte contre la pauvreté. Nous sommes bénévoles, éducateurs et voisins — redevables envers nos membres et transparents sur chaque euro.",
      chapters: [
        { num: "01", title: "Nous partons des jeunes", text: "Chaque programme commence par une question : que faut-il à un ado de 15 ans de ce quartier pour croire en son avenir ? La réponse façonne le budget, pas l'inverse." },
        { num: "02", title: "L'éducation est une infrastructure", text: "Kits, tuteurs, ordinateurs, bourses. Nous traitons le matériel d'apprentissage comme des routes et des ponts — des biens publics qui rapportent pendant des décennies." },
        { num: "03", title: "La dignité avant la charité", text: "Un repas chaud servi avec respect. Un kit d'hiver sans morale. Nous rencontrons les gens là où ils sont, sans conditions attachées à la compassion." },
        { num: "04", title: "Transparence radicale", text: "Chaque campagne publie son objectif, son montant collecté et ses résultats. Nos comptes sont présentés chaque année à l'assemblée générale des membres." },
      ],
      legalTitle: "Identité légale",
      legalText: "Future Gen ASBL · Association sans but lucratif enregistrée au Luxembourg · contact@future-gen.eu · future-gen.eu",
      imageCaption: "Nos bénévoles lors d'un atelier du dimanche.",
    },
    campaignsPage: {
      overline: "Campagnes",
      title: "Objectifs concrets. Euros tracés.",
      sub: "Chaque campagne a une cible, un but et un compteur public. Visitez-en une, voyez exactement ce que votre contribution finance.",
      current: "Campagnes en cours",
      past: "Campagnes passées",
      raised: "collectés",
      of: "sur",
      goal: "objectif",
      completed: "Terminée",
      explore: "Visiter la campagne",
    },
    detail: {
      back: "Toutes les campagnes",
      purpose: "Ce que cela finance",
      raisedOf: "collectés sur",
      goal: "objectif",
      donateTitle: "Contribuer",
      cardTab: "Carte (Stripe)",
      bankTab: "Virement bancaire",
      name: "Votre nom",
      email: "Votre e-mail",
      amount: "Montant (EUR)",
      suggested: "Suggéré",
      stripeNote: "Vous choisirez le montant exact sur la page sécurisée Stripe.",
      donateNow: "Donner en toute sécurité",
      bankInfo: "Effectuez un virement vers notre compte avec la référence ci-dessous — nous confirmons votre promesse dès réception.",
      iban: "IBAN",
      bic: "BIC",
      accountName: "Nom du compte",
      pledgeBtn: "Enregistrer ma promesse",
      pledgeThanks: "Promesse enregistrée ! Utilisez cette référence pour votre virement :",
      reference: "Référence",
      loading: "Chargement…",
      updates: "Actualités de la campagne",
      donorWall: "Derniers soutiens",
      share: "Partager cette campagne",
      copied: "Lien copié !",
      anonymous: "Anonyme",
      anonymousLabel: "Don anonyme — mon nom n'apparaîtra pas dans la liste des soutiens",
    },
    membership: {
      overline: "Adhésion",
      title: "Devenez membre de Future Gen.",
      sub: "Les membres financent la colonne vertébrale de l'association, votent à l'assemblée générale et reçoivent notre lettre d'impact trimestrielle.",
      monthly: { name: "Mensuel", price: "10 €", period: "/mois", note: "Flexible, résiliable à tout moment" },
      yearly: { name: "Annuel", price: "100 €", period: "/an", note: "Un seul geste pour toute l'année" },
      choosePlan: "Choisissez votre contribution",
      details: "Vos coordonnées",
      name: "Nom complet",
      email: "Adresse e-mail",
      method: "Mode de paiement",
      card: "Carte — récurrent via Stripe",
      bank: "Virement — ordre permanent",
      submitCard: "Continuer vers le paiement sécurisé",
      submitBank: "S'inscrire & recevoir les coordonnées",
      successTitle: "Bienvenue !",
      successText: "Votre inscription est enregistrée. Mettez en place votre ordre permanent avec la référence ci-dessous et nous activerons votre adhésion à réception.",
      benefitsTitle: "L'adhésion comprend",
      benefits: ["Vote à l'assemblée générale annuelle", "Proposer des campagnes", "Lettre d'impact trimestrielle", "Invitations aux événements membres", "Rapport financier annuel complet"],
    },
    contact: {
      overline: "Contact",
      title: "Écrivez-nous. Nous répondons.",
      sub: "Une question, une idée de partenariat, une école qui a besoin de nous ? Cette boîte est lue par des humains.",
      name: "Votre nom",
      email: "Votre e-mail",
      subject: "Sujet",
      message: "Votre message",
      send: "Envoyer le message",
      success: "Message envoyé — nous répondrons rapidement.",
      addressLabel: "Nous rendre visite",
      address: "Luxembourg",
      emailLabel: "E-mail",
    },
    payment: {
      successTitle: "Merci. Vraiment.",
      successText: "Votre paiement est confirmé. Vous venez de faire bouger un compteur qui compte.",
      pendingTitle: "Confirmation de votre paiement…",
      pendingText: "Nous vérifions auprès de Stripe. Quelques secondes suffisent.",
      failTitle: "Paiement pas encore confirmé",
      failText: "Votre banque traite peut-être encore. Si vous avez été débité, cela apparaîtra sous peu.",
      backHome: "Retour à l'accueil",
      cancelledTitle: "Paiement annulé",
      cancelledText: "Aucun débit n'a été effectué. La campagne sera toujours là quand vous serez prêt·e.",
      tryAgain: "Retour aux campagnes",
    },
    footer: {
      tagline: "Construire la génération qui construira le futur.",
      explore: "Explorer",
      contactTitle: "Contact",
      legalTitle: "Mentions légales",
      legal: "Future Gen ASBL · Association sans but lucratif enregistrée au Luxembourg",
      newsletterTitle: "Lettre d'impact",
      newsletterPlaceholder: "Votre e-mail",
      newsletterBtn: "S'abonner",
      newsletterThanks: "C'est noté — merci !",
      rights: "Tous droits réservés.",
    },
    common: { loading: "Chargement…" },
  },
  de: {
    nav: { home: "Startseite", about: "Über uns", campaigns: "Kampagnen", suggest: "Kampagne vorschlagen", membership: "Mitglied werden", contact: "Kontakt", donate: "Spenden" },
    suggest: {
      overline: "Ihre Idee, unsere Mission",
      title: "Eine Kampagne vorschlagen",
      sub: "Eine Idee, die Leben verändern könnte? Erzählen Sie uns von der Sache, ihrer Finanzierung und der Verwendung der Mittel.",
      note: "Jeder Vorschlag landet direkt im Postfach unseres Teams unter contact@future-gen.eu. Wir prüfen jeden einzelnen und melden uns persönlich bei Ihnen.",
      memberRequired: "Bitte beachten: Um eine Kampagne vorzuschlagen, müssen Sie Mitglied von Future Gen ASBL sein.",
      memberRequiredLink: "Noch kein Mitglied? Hier beitreten",
      campaignTitle: "Titel der Kampagne",
      description: "Beschreibung der Kampagne — worum geht es, wem hilft sie, warum jetzt?",
      objective: "Ziel der Kampagne",
      fundSource: "Finanzierungsquelle",
      srcDonation: "Spende",
      srcProduct: "Produktverkauf",
      srcService: "Dienstleistungsverkauf",
      srcOther: "Sonstiges",
      fundDistribution: "Mittelverwendung",
      fundDistributionPh: "Wie würden die Mittel verwendet?",
      contactSection: "Wie erreichen wir Sie?",
      contactName: "Name der Kontaktperson",
      contactEmail: "E-Mail der Kontaktperson",
      contactPhone: "Telefon der Kontaktperson",
      submit: "Vorschlag senden",
      successTitle: "Danke!",
      success: "Ihr Vorschlag wurde an unser Team gesendet. Wir melden uns bald bei Ihnen.",
    },
    hero: {
      overline: "Future Gen ASBL — Luxemburg · Jugend · Bildung · Solidarität",
      lines: ["DIE ZUKUNFT", "WIRD GEBAUT", "VON DER JUGEND."],
      sub: "Wir sind ein in Luxemburg eingetragener gemeinnütziger Verein für Jugendförderung, Bildungszugang und Armutsbekämpfung. Jede Kampagne ist konkret, jeder Euro nachverfolgbar.",
      ctaPrimary: "Eine Kampagne unterstützen",
      ctaSecondary: "Unser Manifest lesen",
    },
    marquee: ["FUTURE GEN ASBL", "JUGEND STÄRKEN", "BILDUNG FÜR ALLE", "GEGEN ARMUT"],
    stats: { raised: "für unsere Kampagnen gesammelt", campaigns: "Kampagnen bis heute", members: "aktive Mitglieder", donations: "erhaltene Spenden" },
    pillars: {
      overline: "Wofür wir kämpfen",
      title: "Drei Kämpfe, eine Generation.",
      items: [
        { num: "01", title: "Jugendförderung", text: "Mentoring, Kompetenz-Workshops und sichere Orte, an denen junge Menschen Selbstvertrauen, Können und ein Netzwerk aufbauen, das sie in die Arbeitswelt trägt." },
        { num: "02", title: "Zugang zu Bildung", text: "Schulsets, Nachhilfe, Stipendien und digitale Klassenzimmer — denn Talent ist überall, Chancen sind es nicht." },
        { num: "03", title: "Armutsbekämpfung", text: "Warme Mahlzeiten, Winterpakete und Vermittlung von Unterkünften. Unmittelbare Würde für Nachbarn, die nicht auf Systemwandel warten können." },
      ],
    },
    featured: { overline: "Aktuelle Kampagnen", title: "Wohin Ihr Euro heute geht.", viewAll: "Alle Kampagnen", raised: "gesammelt", of: "von", support: "Diese Kampagne unterstützen", completed: "Abgeschlossen" },
    manifesto: {
      overline: "Unser Manifest",
      title: "Wir erben die Zukunft nicht. Wir bauen sie.",
      text: "Future Gen ASBL entstand aus einer einfachen Überzeugung: Die Generation, die morgen leben wird, verdient heute einen Platz am Tisch. Wir finanzieren, lehren und stehen an der Seite der Jugend — im Klassenzimmer, in der Werkstatt, auf der Straße.",
      cta: "Entdecken Sie, wer wir sind",
    },
    ctaBand: { title: "Bereit, vom Zuschauer zum Mitgestalter zu werden?", text: "Werden Sie Mitglied ab 10 €/Monat oder spenden Sie einmalig für eine Kampagne, die Sie anspricht.", member: "Mitglied werden", donate: "Kampagnen ansehen" },
    about: {
      overline: "Über Future Gen ASBL",
      title: "Ein Verein, geschrieben in der Zukunftsform.",
      intro: "Future Gen ASBL ist ein in Luxemburg eingetragener gemeinnütziger Verein an der Schnittstelle von Jugendförderung, Bildung und Armutsbekämpfung. Wir sind Freiwillige, Pädagogen und Nachbarn — unseren Mitgliedern rechenschaftspflichtig und transparent bei jedem Euro.",
      chapters: [
        { num: "01", title: "Wir beginnen bei den Jungen", text: "Jedes Programm beginnt mit einer Frage: Was braucht ein 15-Jähriger aus diesem Viertel, um an seine Zukunft zu glauben? Die Antwort formt das Budget, nicht umgekehrt." },
        { num: "02", title: "Bildung ist Infrastruktur", text: "Sets, Tutoren, Laptops, Stipendien. Wir behandeln Lernmaterial wie Straßen und Brücken — öffentliche Güter, die über Jahrzehnte wirken." },
        { num: "03", title: "Würde vor Wohltätigkeit", text: "Eine warme Mahlzeit, mit Respekt serviert. Ein Winterpaket ohne Belehrung. Wir begegnen Menschen dort, wo sie sind — Mitgefühl ohne Bedingungen." },
        { num: "04", title: "Radikale Transparenz", text: "Jede Kampagne veröffentlicht ihr Ziel, den gesammelten Betrag und ihre Ergebnisse. Unsere Konten werden jährlich der Mitgliederversammlung vorgelegt." },
      ],
      legalTitle: "Rechtliche Identität",
      legalText: "Future Gen ASBL · Gemeinnütziger Verein, eingetragen in Luxemburg · contact@future-gen.eu · future-gen.eu",
      imageCaption: "Unsere Freiwilligen bei einem Sonntags-Workshop.",
    },
    campaignsPage: {
      overline: "Kampagnen",
      title: "Konkrete Ziele. Nachverfolgte Euros.",
      sub: "Jede Kampagne hat ein Ziel, einen Zweck und einen öffentlichen Zähler. Besuchen Sie eine und sehen Sie genau, was Ihr Beitrag finanziert.",
      current: "Laufende Kampagnen",
      past: "Vergangene Kampagnen",
      raised: "gesammelt",
      of: "von",
      goal: "Ziel",
      completed: "Abgeschlossen",
      explore: "Kampagne besuchen",
    },
    detail: {
      back: "Alle Kampagnen",
      purpose: "Was damit finanziert wird",
      raisedOf: "gesammelt von",
      goal: "Ziel",
      donateTitle: "Beitragen",
      cardTab: "Karte (Stripe)",
      bankTab: "Überweisung",
      name: "Ihr Name",
      email: "Ihre E-Mail",
      amount: "Betrag (EUR)",
      suggested: "Vorschlag",
      stripeNote: "Den genauen Betrag wählen Sie auf der sicheren Stripe-Seite.",
      donateNow: "Sicher spenden",
      bankInfo: "Überweisen Sie auf unser Konto mit der untenstehenden Referenz — wir bestätigen Ihre Zusage nach Eingang.",
      iban: "IBAN",
      bic: "BIC",
      accountName: "Kontoinhaber",
      pledgeBtn: "Meine Zusage registrieren",
      pledgeThanks: "Zusage registriert! Verwenden Sie diese Referenz für Ihre Überweisung:",
      reference: "Referenz",
      loading: "Laden…",
      updates: "Neuigkeiten zur Kampagne",
      donorWall: "Letzte Unterstützer",
      share: "Diese Kampagne teilen",
      copied: "Link kopiert!",
      anonymous: "Anonym",
      anonymousLabel: "Anonym spenden — mein Name erscheint nicht in der Unterstützerliste",
    },
    membership: {
      overline: "Mitgliedschaft",
      title: "Werden Sie Mitglied von Future Gen.",
      sub: "Mitglieder finanzieren das Rückgrat des Vereins, stimmen in der Mitgliederversammlung ab und erhalten unseren vierteljährlichen Impact-Brief.",
      monthly: { name: "Monatlich", price: "10 €", period: "/Monat", note: "Flexibel, jederzeit kündbar" },
      yearly: { name: "Jährlich", price: "100 €", period: "/Jahr", note: "Eine Geste für ein ganzes Jahr" },
      choosePlan: "Wählen Sie Ihren Beitrag",
      details: "Ihre Angaben",
      name: "Vollständiger Name",
      email: "E-Mail-Adresse",
      method: "Zahlungsmethode",
      card: "Karte — wiederkehrend über Stripe",
      bank: "Überweisung — Dauerauftrag",
      submitCard: "Weiter zur sicheren Zahlung",
      submitBank: "Registrieren & Bankdaten erhalten",
      successTitle: "Willkommen an Bord!",
      successText: "Ihre Anmeldung ist erfasst. Richten Sie Ihren Dauerauftrag mit der untenstehenden Referenz ein — wir aktivieren Ihre Mitgliedschaft nach Eingang.",
      benefitsTitle: "Die Mitgliedschaft umfasst",
      benefits: ["Stimmrecht in der jährlichen Mitgliederversammlung", "Kampagnenvorschläge einreichen", "Vierteljährlicher Impact-Brief", "Einladungen zu Mitgliederveranstaltungen", "Vollständiger Jahresfinanzbericht"],
    },
    contact: {
      overline: "Kontakt",
      title: "Schreiben Sie uns. Wir antworten.",
      sub: "Eine Frage, eine Partnerschaftsidee, eine Schule, die uns braucht? Dieses Postfach wird von Menschen gelesen.",
      name: "Ihr Name",
      email: "Ihre E-Mail",
      subject: "Betreff",
      message: "Ihre Nachricht",
      send: "Nachricht senden",
      success: "Nachricht gesendet — wir antworten in Kürze.",
      addressLabel: "Besuchen Sie uns",
      address: "Luxemburg",
      emailLabel: "E-Mail",
    },
    payment: {
      successTitle: "Danke. Von Herzen.",
      successText: "Ihre Zahlung ist bestätigt. Sie haben gerade einen Zähler bewegt, der zählt.",
      pendingTitle: "Ihre Zahlung wird bestätigt…",
      pendingText: "Wir prüfen bei Stripe. Das dauert nur wenige Sekunden.",
      failTitle: "Zahlung noch nicht bestätigt",
      failText: "Ihre Bank verarbeitet möglicherweise noch. Falls Sie belastet wurden, erscheint es in Kürze.",
      backHome: "Zurück zur Startseite",
      cancelledTitle: "Zahlung abgebrochen",
      cancelledText: "Es wurde nichts abgebucht. Die Kampagne ist weiterhin hier, wenn Sie bereit sind.",
      tryAgain: "Zurück zu den Kampagnen",
    },
    footer: {
      tagline: "Wir bauen die Generation, die die Zukunft baut.",
      explore: "Entdecken",
      contactTitle: "Kontakt",
      legalTitle: "Rechtliches",
      legal: "Future Gen ASBL · Gemeinnütziger Verein, eingetragen in Luxemburg",
      newsletterTitle: "Impact-Newsletter",
      newsletterPlaceholder: "Ihre E-Mail",
      newsletterBtn: "Abonnieren",
      newsletterThanks: "Sie sind dabei — danke!",
      rights: "Alle Rechte vorbehalten.",
    },
    common: { loading: "Laden…" },
  },
  tr: {
    nav: { home: "Ana Sayfa", about: "Hakkımızda", campaigns: "Kampanyalar", suggest: "Kampanya Öner", membership: "Üye Ol", contact: "İletişim", donate: "Bağış Yap" },
    suggest: {
      overline: "Sizin fikriniz, bizim misyonumuz",
      title: "Bir kampanya önerin",
      sub: "Hayatları değiştirebilecek bir fikriniz mi var? Bize amacı, nasıl finanse edileceğini ve fonların nasıl kullanılacağını anlatın.",
      note: "Her öneri doğrudan ekibimizin contact@future-gen.eu adresine ulaşır. Her birini inceler ve size kişisel olarak dönüş yaparız.",
      memberRequired: "Lütfen dikkat: Kampanya önermek için Future Gen ASBL üyesi olmanız gerekir.",
      memberRequiredLink: "Henüz üye değil misiniz? Buradan katılın",
      campaignTitle: "Kampanya başlığı",
      description: "Kampanya açıklaması — amaç nedir, kime yardım eder, neden şimdi?",
      objective: "Kampanya hedefi",
      fundSource: "Fon kaynağı",
      srcDonation: "Bağış",
      srcProduct: "Ürün satışı",
      srcService: "Hizmet satışı",
      srcOther: "Diğer",
      fundDistribution: "Fon dağılımı",
      fundDistributionPh: "Fonlar nasıl kullanılacak?",
      contactSection: "Size nasıl ulaşabiliriz?",
      contactName: "İletişim adı",
      contactEmail: "İletişim e-postası",
      contactPhone: "İletişim telefonu",
      submit: "Öneriyi gönder",
      successTitle: "Teşekkürler!",
      success: "Öneriniz ekibimize gönderildi. En kısa sürede size dönüş yapacağız.",
    },
    hero: {
      overline: "Future Gen ASBL — Lüksemburg · Gençlik · Eğitim · Dayanışma",
      lines: ["GELECEĞİ", "GENÇLER", "İNŞA EDER."],
      sub: "Lüksemburg'da kayıtlı, gençliğin gelişimi, eğitime erişim ve yoksullukla mücadele için çalışan bir dernek olarak her kampanyamız somut, her euro izlenebilir.",
      ctaPrimary: "Bir kampanyayı destekle",
      ctaSecondary: "Manifestomuzu okuyun",
    },
    marquee: ["FUTURE GEN ASBL", "GENÇLİĞE GÜÇ", "HERKES İÇİN EĞİTİM", "YOKSULLUKLA MÜCADELE"],
    stats: { raised: "kampanyalarımız için toplandı", campaigns: "bugüne kadar yürütülen kampanya", members: "aktif üye", donations: "alınan bağış" },
    pillars: {
      overline: "Neyin için mücadele ediyoruz",
      title: "Üç mücadele, tek nesil.",
      items: [
        { num: "01", title: "Gençliğin Gelişimi", text: "Mentorluk, beceri atölyeleri ve gençlerin özgüven, ustalık ve onları iş hayatına taşıyacak bir çevre edindiği güvenli alanlar." },
        { num: "02", title: "Eğitime Erişim", text: "Okul setleri, özel dersler, burslar ve dijital sınıflar — çünkü yetenek her yerde, ama fırsat değil." },
        { num: "03", title: "Yoksullukla Mücadele", text: "Sıcak yemekler, kış paketleri ve barınma yönlendirmeleri. Sistemin değişmesini bekleyemeyecek komşularımız için anında onur." },
      ],
    },
    featured: { overline: "Günün kampanyaları", title: "Bugün euronuz nereye gidiyor.", viewAll: "Tüm kampanyalar", raised: "toplandı", of: "/", support: "Bu kampanyayı destekle", completed: "Tamamlandı" },
    manifesto: {
      overline: "Manifestomuz",
      title: "Geleceği miras almıyoruz. Onu inşa ediyoruz.",
      text: "Future Gen ASBL basit bir inançla kuruldu: Yarını yaşayacak nesil, bugün masada bir yeri hak ediyor. Gençleri finanse ediyor, eğitiyor ve yanlarında duruyoruz — sınıfta, atölyede, sokakta.",
      cta: "Kim olduğumuzu keşfedin",
    },
    ctaBand: { title: "İzleyici olmaktan inşa eden olmaya hazır mısınız?", text: "Ayda 10 €'dan üye olun ya da size dokunan bir kampanyaya tek seferlik bağış yapın.", member: "Üye ol", donate: "Kampanyaları gör" },
    about: {
      overline: "Future Gen ASBL hakkında",
      title: "Gelecek zamanda yazılmış bir dernek.",
      intro: "Lüksemburg'da kayıtlı Future Gen ASBL; gençliğin gelişimi, eğitim ve yoksullukla mücadelenin kesişiminde çalışan bir dernektir. Bizler gönüllüler, eğitimciler ve komşularız — üyelerimize hesap verir, her euroda şeffafız.",
      chapters: [
        { num: "01", title: "Gençlerden başlıyoruz", text: "Her program bir soruyla başlar: Bu mahalledeki 15 yaşındaki bir gencin kendi geleceğine inanması için neye ihtiyacı var? Bütçeyi bu cevap şekillendirir, tersi değil." },
        { num: "02", title: "Eğitim bir altyapıdır", text: "Setler, öğretmenler, bilgisayarlar, burslar. Öğrenme materyallerini yollar ve köprüler gibi görürüz — on yıllarca kazandıran kamu malları." },
        { num: "03", title: "Yardımdan önce onur", text: "Saygıyla sunulan sıcak bir yemek. Nasihatsiz bir kış paketi. İnsanlarla oldukları yerde, şefkate koşul koymadan buluşuruz." },
        { num: "04", title: "Radikal şeffaflık", text: "Her kampanya hedefini, toplanan tutarı ve sonuçlarını yayınlar. Hesaplarımız her yıl üyeler genel kuruluna sunulur." },
      ],
      legalTitle: "Yasal kimlik",
      legalText: "Future Gen ASBL · Lüksemburg'da kayıtlı kâr amacı gütmeyen dernek · contact@future-gen.eu · future-gen.eu",
      imageCaption: "Pazar atölyesinde gönüllülerimiz.",
    },
    campaignsPage: {
      overline: "Kampanyalar",
      title: "Somut hedefler. İzlenen eurolar.",
      sub: "Her kampanyanın bir hedefi, bir amacı ve herkese açık bir sayacı var. Birini ziyaret edin, katkınızın tam olarak neyi finanse ettiğini görün.",
      current: "Devam eden kampanyalar",
      past: "Geçmiş kampanyalar",
      raised: "toplandı",
      of: "/",
      goal: "hedef",
      completed: "Tamamlandı",
      explore: "Kampanyayı ziyaret et",
    },
    detail: {
      back: "Tüm kampanyalar",
      purpose: "Neleri finanse ediyor",
      raisedOf: "toplandı / hedef:",
      goal: "hedef",
      donateTitle: "Katkıda bulun",
      cardTab: "Kart (Stripe)",
      bankTab: "Banka havalesi",
      name: "Adınız",
      email: "E-postanız",
      amount: "Tutar (EUR)",
      suggested: "Önerilen",
      stripeNote: "Kesin tutarı güvenli Stripe sayfasında seçeceksiniz.",
      donateNow: "Güvenle bağış yap",
      bankInfo: "Aşağıdaki referansla hesabımıza havale yapın — ödeme ulaştığında taahhüdünüzü onaylarız.",
      iban: "IBAN",
      bic: "BIC",
      accountName: "Hesap adı",
      pledgeBtn: "Taahhüdümü kaydet",
      pledgeThanks: "Taahhüt kaydedildi! Havaleniz için bu referansı kullanın:",
      reference: "Referans",
      loading: "Yükleniyor…",
      updates: "Kampanya haberleri",
      donorWall: "Son destekçiler",
      share: "Bu kampanyayı paylaş",
      copied: "Bağlantı kopyalandı!",
      anonymous: "Anonim",
      anonymousLabel: "Bağışım anonim olsun — adım destekçiler listesinde görünmesin",
    },
    membership: {
      overline: "Üyelik",
      title: "Future Gen üyesi olun.",
      sub: "Üyeler derneğin omurgasını finanse eder, genel kurulda oy kullanır ve üç aylık etki bültenimizi alır.",
      monthly: { name: "Aylık", price: "10 €", period: "/ay", note: "Esnek, istediğiniz zaman iptal" },
      yearly: { name: "Yıllık", price: "100 €", period: "/yıl", note: "Bütün bir yıl için tek jest" },
      choosePlan: "Katkınızı seçin",
      details: "Bilgileriniz",
      name: "Ad Soyad",
      email: "E-posta adresi",
      method: "Ödeme yöntemi",
      card: "Kart — Stripe ile düzenli ödeme",
      bank: "Havale — düzenli ödeme talimatı",
      submitCard: "Güvenli ödemeye devam et",
      submitBank: "Kaydol & banka bilgilerini al",
      successTitle: "Aramıza hoş geldiniz!",
      successText: "Kaydınız alındı. Aşağıdaki referansla düzenli ödeme talimatınızı oluşturun; ödeme ulaştığında üyeliğinizi etkinleştireceğiz.",
      benefitsTitle: "Üyelik şunları içerir",
      benefits: ["Yıllık genel kurulda oy hakkı", "Kampanya önerisi sunma", "Üç aylık etki bülteni", "Üye etkinliklerine davet", "Tam yıllık mali rapor"],
    },
    contact: {
      overline: "İletişim",
      title: "Bize yazın. Cevap veriyoruz.",
      sub: "Bir soru, bir iş birliği fikri, bize ihtiyacı olan bir okul mu var? Bu kutu insanlar tarafından okunuyor.",
      name: "Adınız",
      email: "E-postanız",
      subject: "Konu",
      message: "Mesajınız",
      send: "Mesajı gönder",
      success: "Mesaj gönderildi — kısa süre içinde yanıtlayacağız.",
      addressLabel: "Bizi ziyaret edin",
      address: "Lüksemburg",
      emailLabel: "E-posta",
    },
    payment: {
      successTitle: "Teşekkürler. Gerçekten.",
      successText: "Ödemeniz onaylandı. Önemli bir sayacı az önce siz ilerlettiniz.",
      pendingTitle: "Ödemeniz onaylanıyor…",
      pendingText: "Stripe ile doğruluyoruz. Birkaç saniye sürer.",
      failTitle: "Ödeme henüz onaylanmadı",
      failText: "Bankanız hâlâ işliyor olabilir. Ödeme alındıysa kısa süre içinde görünecektir.",
      backHome: "Ana sayfaya dön",
      cancelledTitle: "Ödeme iptal edildi",
      cancelledText: "Herhangi bir tahsilat yapılmadı. Hazır olduğunuzda kampanya burada olacak.",
      tryAgain: "Kampanyalara dön",
    },
    footer: {
      tagline: "Geleceği inşa edecek nesli inşa ediyoruz.",
      explore: "Keşfet",
      contactTitle: "İletişim",
      legalTitle: "Yasal",
      legal: "Future Gen ASBL · Lüksemburg'da kayıtlı kâr amacı gütmeyen dernek",
      newsletterTitle: "Etki bülteni",
      newsletterPlaceholder: "E-postanız",
      newsletterBtn: "Abone ol",
      newsletterThanks: "Kaydınız alındı — teşekkürler!",
      rights: "Tüm hakları saklıdır.",
    },
    common: { loading: "Yükleniyor…" },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("fg_lang") : null;
    return ["en", "fr", "de", "tr"].includes(saved) ? saved : "en";
  });
  const setLang = (l) => {
    setLangState(l);
    try { window.localStorage.setItem("fg_lang", l); } catch { /* ignore */ }
  };
  const clang = lang === "fr" ? "fr" : "en";
  const cf = (obj, field) => {
    if (!obj) return "";
    const pick = (l) => {
      const v = obj[`${field}_${l}`];
      return v && (!Array.isArray(v) || v.length) ? v : null;
    };
    return pick(lang) ?? pick(clang) ?? pick("en") ?? "";
  };
  const t = (path) => {
    const val = path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), translations[lang]);
    if (val !== undefined) return val;
    const fallback = path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), translations.en);
    return fallback === undefined ? path : fallback;
  };
  return <LanguageContext.Provider value={{ lang, setLang, clang, cf, t }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

const EUR_LOCALES = { fr: "fr-BE", de: "de-DE", tr: "tr-TR" };

export const fmtEUR = (amount, lang) =>
  new Intl.NumberFormat(EUR_LOCALES[lang] || "en-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount || 0);
